import React, { useEffect, useState, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import qs from 'qs'
import deviceSizes from 'Components/PreviewTool/support/deviceSizes'
import './PreviewMessage.scss'
import PreviewTools, {
  TabAllPreviews,
  Tabs,
  TabsDesktopOrMobile,
  TabShowDynamicContent
} from 'Components/PreviewTool'
import {
  useRecoilCallback,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil'
import {
  filtersState,
  groupsState,
  previewLastSelected,
  previewState
} from './recoil/state/preview'
import { modalBuyCollaborationState } from './recoil/state/modals'
import {
  initPreviewCallbacks,
  searchRecipientsCallback,
  updateDynamicContentsCallback
} from './recoil/callbacks/callbacks'
import ModalSendingMessage from './components/ModalSendingMessage'
import HeadComponent from './components/HeadComponent'
import ModalBuyCollaboration from 'Components/ModalBuyCollaboration'

import {
  ToastContainer,
  SpacingVertical,
  SPACING_40,
  SPACING_24
} from 'mailup-web-components'
import { parseBooleanQuery } from 'boolean-parser'
import ShowPersonalization from './components/ShowPersonalization/ShowPersonalization'
import styled from '@emotion/styled'
import SelectRecipient from 'Components/PreviewTool/SelectRecipient'

const ModalBuyCollaborationWrapper = () => {
  const { show } = useRecoilValue(modalBuyCollaborationState)

  const handleClose = useResetRecoilState(modalBuyCollaborationState)

  return <ModalBuyCollaboration show={show} onClose={handleClose} />
}

const PreviewMessage = () => {
  const { id } = useParams()
  const { search } = useLocation()
  const footerRef = useRef(null)
  const qsParams = qs.parse(
    search.startsWith('?') ? search.substring(1) : search
  )
  const crossLists = Boolean(parseInt(qsParams.crossLists))
  const fromOverview = Boolean(parseInt(qsParams.fm))
  const overviewNotSave = Boolean(parseInt(qsParams.ons))
  const hideAllPreview = Boolean(parseInt(qsParams.hideAllPreview))
  const goToFooter = qsParams.goToFooter
    ? Boolean(parseBooleanQuery(qsParams.goToFooter))
    : false

  const hasAllPreviews = !(crossLists || hideAllPreview || overviewNotSave)

  const {
    loading,
    error,
    content,
    isBee,
    idList,
    recipient,
    showPersonalization,
    dynamicContents,
    selectedDynamicContents
  } = useRecoilValue(previewState)

  const setShowPersonalization = useSetRecoilState(previewState)

  const {
    lastEmails,
    lastFilters,
    lastGroups,
    previewReferenceRecipient
  } = useRecoilValue(previewLastSelected)
  const { options, optionGroups } = useRecoilValue(filtersState)
  const groups = useRecoilValue(groupsState)
  const searchRecipients = useRecoilCallback(searchRecipientsCallback, [
    previewReferenceRecipient
  ])

  const [deviceType, setDeviceType] = useState('desktop')
  const [sizeId, setSizeId] = useState(deviceSizes[0].id)

  const initPreview = useRecoilCallback(initPreviewCallbacks, [])

  const handleConfirmDynamicContents = useRecoilCallback(
    updateDynamicContentsCallback,
    []
  )

  useEffect(() => {
    initPreview({ id, crossLists, fromOverview })
  }, [])

  const size = deviceSizes.find(size => size.id === sizeId)

  const hidePersonalization = () => {
    setShowPersonalization(prev => ({
      ...prev,
      showPersonalization: false
    }))
  }

  if (goToFooter) {
    setTimeout(() => {
      footerRef.current.scrollIntoView({ behavior: 'smooth' })
    }, 1000)
  }

  const TabsComponent = () => (
    <Tabs>
      <TabsDesktopOrMobile
        active={deviceType}
        onClickTab={value => setDeviceType(value)}
        onChangeSelect={value => setSizeId(value)}
        selectValue={sizeId}
        showMobile={isBee}
      />
      {hasAllPreviews && <TabAllPreviews idMessage={id} idList={idList} />}
      {dynamicContents.length > 0 && (
        <TabShowDynamicContent
          onConfirm={handleConfirmDynamicContents}
          items={dynamicContents}
          initialValues={selectedDynamicContents}
        />
      )}
    </Tabs>
  )

  return (
    <>
      <ModalSendingMessage />
      <ModalBuyCollaborationWrapper />
      <ToastContainer />
      <PreviewTools
        TabsComponent={TabsComponent}
        HeadComponent={HeadComponent}
        content={content}
        loading={loading}
        error={error}
        previewers={
          showPersonalization
            ? undefined
            : [
                {
                  sandbox:
                    'allow-same-origin allow-popups allow-popups-to-escape-sandbox',
                  content: content,
                  active: deviceType === 'desktop'
                },
                {
                  sandbox:
                    'allow-same-origin allow-popups allow-popups-to-escape-sandbox',
                  content: content,
                  width: size.width,
                  height: size.height,
                  pixelRatio: size.pixelRatio,
                  active: deviceType === 'mobile'
                }
              ]
        }
      >
        <StyledSection showPersonalization={showPersonalization}>
          {recipient ? (
            <ShowPersonalization onClose={hidePersonalization} />
          ) : (
            <>
              <SpacingVertical variant={SPACING_40} />
              <SpacingVertical variant={SPACING_24} />
            </>
          )}
          <SelectRecipient
            groups={groups}
            filters={options}
            filterGroups={optionGroups}
            lastEmails={lastEmails}
            lastFilters={lastFilters}
            lastGroups={lastGroups}
            lastTypeSelected={previewReferenceRecipient.lastTypeSelected}
            searchRecipientsCallback={searchRecipients}
          />
        </StyledSection>
      </PreviewTools>
      <div ref={footerRef} />
    </>
  )
}

export default PreviewMessage

const StyledSection = styled.div`
  position: relative;
  background-color: var(--pure-white);

  ${({ showPersonalization }) => !showPersonalization && 'display: none'}
`
